import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { ShieldCheck, Gift, Search, RefreshCw } from "lucide-react";
import api, { formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATE_BADGE = {
  exempt:  "bg-purple-50 text-purple-700 border-purple-200",
  active:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  trial:   "bg-blue-50 text-blue-700 border-blue-200",
  expired: "bg-red-50 text-red-700 border-red-200",
};

export default function AdminSubscriptions() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [showActivate, setShowActivate] = useState(null);
  const [days, setDays] = useState(365);
  const [note, setNote] = useState("");
  const [paymentRef, setPaymentRef] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!user?.is_superadmin) { nav("/"); return; }
    let cancelled = false;
    api.get("/subscription/admin/users").then((r) => {
      if (cancelled) return;
      setUsers(r.data || []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [user, nav, refreshTick]);

  const filtered = useMemo(() => {
    if (!q) return users;
    const s = q.toLowerCase();
    return users.filter((u) => (u.email || "").toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s));
  }, [users, q]);

  const counts = useMemo(() => {
    const c = { trial: 0, active: 0, expired: 0, exempt: 0 };
    users.forEach((u) => { if (c[u.state] != null) c[u.state]++; });
    return c;
  }, [users]);

  const activate = async () => {
    if (!showActivate) return;
    setBusy(true);
    try {
      const r = await api.post("/subscription/admin/activate", {
        user_email: showActivate.email,
        days: Number(days) || 365,
        note,
        payment_ref: paymentRef,
      });
      toast.success(`Activated ${r.data.user_email} until ${new Date(r.data.extended_to).toLocaleDateString("en-IN")} (+${r.data.days_added} days)`);
      setShowActivate(null);
      setNote(""); setPaymentRef(""); setDays(365);
      setRefreshTick((x) => x + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation failed");
    } finally {
      setBusy(false);
    }
  };

  if (!user?.is_superadmin) return null;

  return (
    <>
      <PageHeader
        title="Subscriptions (Admin)"
        subtitle="View all users · grant subscriptions manually (offline payment / UPI / comp accounts)"
        actions={
          <Button variant="outline" onClick={() => setRefreshTick((x) => x + 1)}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        }
      />
      <div className="p-6 space-y-4" data-testid="admin-subs-page">
        {/* Top stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {["active", "trial", "expired", "exempt"].map((k) => (
            <div key={k} className={`border rounded p-3 ${STATE_BADGE[k]}`}>
              <div className="text-[10px] uppercase opacity-70">{k}</div>
              <div className="text-2xl font-bold">{counts[k]}</div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-gray-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or name…" className="max-w-sm h-8 text-sm" data-testid="admin-subs-search" />
        </div>

        {loading && <div className="text-sm text-gray-500">Loading…</div>}

        {!loading && (
          <div className="border border-gray-200 rounded bg-white overflow-hidden">
            <table className="w-full text-xs" data-testid="admin-subs-table">
              <thead>
                <tr className="text-[10px] uppercase text-gray-500 border-b border-gray-200 bg-gray-50">
                  <th className="text-left pl-3 py-2 font-medium">User</th>
                  <th className="text-left py-2 font-medium">Role</th>
                  <th className="text-center py-2 font-medium">State</th>
                  <th className="text-right py-2 font-medium w-28">Days left</th>
                  <th className="text-left py-2 font-medium w-32">Expires</th>
                  <th className="text-left py-2 font-medium w-32">Last paid</th>
                  <th className="text-right py-2 pr-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`admin-sub-row-${u.email}`}>
                    <td className="pl-3 py-2">
                      <div className="font-medium text-gray-900">{u.name || u.email}</div>
                      <div className="text-[10px] text-gray-500 font-mono">{u.email}</div>
                    </td>
                    <td className="py-2 text-gray-700">
                      {u.is_superadmin ? <span className="inline-flex items-center gap-1 text-purple-700"><ShieldCheck className="w-3 h-3" />superadmin</span> : u.role}
                    </td>
                    <td className="py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-semibold ${STATE_BADGE[u.state]}`}>
                        {u.state}
                      </span>
                    </td>
                    <td className="py-2 text-right font-mono text-gray-700">{u.days_remaining}</td>
                    <td className="py-2 text-gray-700">
                      {u.subscription_expires_at ? new Date(u.subscription_expires_at).toLocaleDateString("en-IN") :
                       u.trial_expires_at ? <span className="text-blue-600">trial · {new Date(u.trial_expires_at).toLocaleDateString("en-IN")}</span> :
                       "—"}
                    </td>
                    <td className="py-2 text-gray-700">
                      {u.last_paid_at ? new Date(u.last_paid_at).toLocaleDateString("en-IN") : "—"}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {!u.is_superadmin && (
                        <Button size="sm" variant="outline" onClick={() => setShowActivate(u)} data-testid={`btn-grant-${u.email}`}>
                          <Gift className="w-3.5 h-3.5 mr-1" /> Grant
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Grant modal */}
        {showActivate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="grant-modal">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <Gift className="w-5 h-5 text-blue-700" /> Grant Subscription
              </h2>
              <p className="text-xs text-gray-600 mb-4">
                Manually activate subscription for <span className="font-mono font-semibold text-gray-900">{showActivate.email}</span>.
                The expiry will extend by the chosen number of days from {showActivate.subscription_expires_at ? "current expiry" : "today"}.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-700">Days to add</label>
                  <Input type="number" value={days} onChange={(e) => setDays(e.target.value)} className="h-8 text-sm" data-testid="grant-days" />
                  <p className="text-[10px] text-gray-500 mt-0.5">365 = full annual subscription. Use shorter periods for support credits.</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">Payment reference</label>
                  <Input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} placeholder="UPI txn id / Bank ref / 'comp'" className="h-8 text-sm" data-testid="grant-ref" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">Internal note</label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. UPI transfer received offline, customer support credit, etc."
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm min-h-16"
                    data-testid="grant-note"
                  />
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowActivate(null)} disabled={busy}>Cancel</Button>
                <Button onClick={activate} disabled={busy} data-testid="btn-grant-confirm">
                  {busy ? "Activating…" : `Activate +${days} days`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
